// Caps how many alert toasts stack on screen at once. A burst — e.g. changing home or
// adding a saved place replays every already-known incident as a fresh alert — used to bury
// the UI under a screen-full of toasts. The stack shows at most `max` of them and folds the
// rest into a single "+N more" row that opens the Now tab. Every toast is click-dismissable.

export interface ToastStackOpts {
  /** Max alert toasts visible at once (the overflow row is extra). Default 3. */
  max?: number;
  /** Label for the overflow row given the number of folded-away toasts. */
  overflowText: (n: number) => string;
  /** Tap handler for the overflow row (e.g. jump to the Now tab). */
  onOverflow?: () => void;
}

export interface ToastAddOpts {
  /** Auto-dismiss after this many ms; ≤ 0 keeps it until clicked. Default 8000. */
  autoDismissMs?: number;
  /** Click handler (e.g. open the incident). The toast is dismissed after it runs. */
  onClick?: () => void;
}

const DEFAULT_DISMISS_MS = 8000;

export class ToastStack {
  #host: HTMLElement;
  #max: number;
  #overflowText: (n: number) => string;
  #onOverflow?: () => void;
  #visible = new Set<HTMLElement>();
  #overflowEl: HTMLElement | null = null;
  #overflowN = 0;
  #overflowTimer: ReturnType<typeof setTimeout> | undefined;

  constructor(host: HTMLElement, opts: ToastStackOpts) {
    this.#host = host;
    this.#max = opts.max ?? 3;
    this.#overflowText = opts.overflowText;
    this.#onOverflow = opts.onOverflow;
  }

  /** Add an alert toast. Shown if under the cap, otherwise folded into the "+N more" row. */
  add(el: HTMLElement, opts: ToastAddOpts = {}): void {
    if (this.#visible.size >= this.#max) { this.#bumpOverflow(); return; }
    this.#visible.add(el);
    if (opts.onClick) {
      el.tabIndex = 0;
      el.style.cursor = "pointer";
      el.addEventListener("click", () => { opts.onClick?.(); this.#dismiss(el); });
    }
    this.#host.append(el);
    // Keep the overflow row last so it reads as a footer beneath the live toasts.
    if (this.#overflowEl) this.#host.append(this.#overflowEl);
    const ms = opts.autoDismissMs ?? DEFAULT_DISMISS_MS;
    // wallclock-allow: browser UI cadence (apps/webapp is gate-30 excluded)
    if (ms > 0 && typeof setTimeout !== "undefined") setTimeout(() => this.#dismiss(el), ms);
  }

  #dismiss(el: HTMLElement): void {
    if (!this.#visible.delete(el)) return;
    el.remove();
  }

  #bumpOverflow(): void {
    this.#overflowN += 1;
    if (!this.#overflowEl) {
      const row = document.createElement("div");
      row.className = "bx-toast bx-toast--overflow";
      row.setAttribute("role", "status");
      row.setAttribute("data-toast", "overflow");
      row.tabIndex = 0;
      row.style.cursor = "pointer";
      row.addEventListener("click", () => { this.#onOverflow?.(); this.#clearOverflow(); });
      this.#overflowEl = row;
    }
    this.#overflowEl.textContent = this.#overflowText(this.#overflowN);
    this.#host.append(this.#overflowEl); // (re)attach + keep last
    if (this.#overflowTimer) clearTimeout(this.#overflowTimer);
    // wallclock-allow: browser UI cadence (apps/webapp is gate-30 excluded)
    if (typeof setTimeout !== "undefined") this.#overflowTimer = setTimeout(() => this.#clearOverflow(), 9000);
  }

  #clearOverflow(): void {
    if (this.#overflowTimer) { clearTimeout(this.#overflowTimer); this.#overflowTimer = undefined; }
    this.#overflowEl?.remove();
    this.#overflowEl = null;
    this.#overflowN = 0;
  }
}
